import { Dropdown, Option, OptionOnSelectData } from '@fluentui/react-components'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { Button } from '../Button/Button'
import { ErrorComponent } from '../ErrorComponent'
import { Loading } from '../Loading/Loading'
import './AddUsersToWorkspaceForm.scss'
import { RolesAssignmentComponent } from './RolesAssignmentComponent'

type AddUserToWorkspaceFormProps = {
  selectedUser?: RestSchemas['Messages.Projects.ProjectUser'] | undefined
  projectId: number
  orgId: number
  appId: number
  workspaceId: number
  onSubmit?: () => void
}

export const AddUserToWorkspaceForm: React.FC<AddUserToWorkspaceFormProps> = ({
  selectedUser,
  projectId,
  orgId,
  appId,
  workspaceId,
  onSubmit,
}) => {
  console.log('selectedUser', selectedUser)
  const queryClient = useQueryClient()
  const edit = !!selectedUser
  const {
    getApplicationsApiListApplicationRoles,
    getWorkspacesApiGetUsers,
    getSubscriptionsApiListOrganisationUsers,
    putProjectsApiAddUserToProject,
    putProjectsApiUpdateUserFromProject,
  } = createRestClient()

  const {
    data: workspaceUsers,
    // isPending: usersPending,
    // isError: usersError,
  } = getWorkspacesApiGetUsers.useQuery({
    query: { workspaceId },
  })

  const orgUsersSelect = useCallback(
    (data: RestSchemas['Messages.Subscriptions.OrganisationUser'][]) => {
      const usersWithMemberRoles = data.map((user) => ({ ...user, memberRoles: [] }))
      const projectUserIds = new Set(workspaceUsers?.map((user) => user.id) || [])
      return usersWithMemberRoles.filter((user) => !projectUserIds.has(user.id))
    },
    [workspaceUsers]
  )

  const {
    data: unassignedOrgUsers,
    isPending: unassignedOrgUsersPending,
    isError: unassignedOrgUsersError,
  } = getSubscriptionsApiListOrganisationUsers.useQuery(
    {
      query: { orgId },
    },
    { select: orgUsersSelect, enabled: !!workspaceUsers }
  )

  const invalidateWorkspaceUsers = () => {
    getWorkspacesApiGetUsers.invalidateQueries({ parameters: { query: { workspaceId } } }, queryClient)
  }

  const mappedAppRolesSelector = useCallback(
    (data: RestSchemas['Messages.Applications.ApplicationRole'][]) =>
      data.map((role) => ({
        roleId: role.id,
        roleName: role.name || 'Default Role Name',
        roleDescription: role.description || 'No Description',
      })),
    []
  )

  const {
    data: mappedAppRoles,
    isPending: appRolesPending,
    isError: appRolesError,
  } = getApplicationsApiListApplicationRoles.useQuery(
    {
      query: { applicationId: appId },
    },
    {
      select: mappedAppRolesSelector,
    }
  )

  const { mutate: addUser, isPending: addUserPending } = putProjectsApiAddUserToProject.useMutation()

  const { mutate: updateUser, isPending: updateUserPending } = putProjectsApiUpdateUserFromProject.useMutation()

  const [selectedUserData, setSelectedUserData] = useState<RestSchemas['Messages.Projects.ProjectUser'] | null>(
    () => selectedUser ?? null
  )
  const [assignedRoleIds, setAssignedRoleIds] = useState<number[]>(
    () => selectedUser?.memberRoles?.map((role) => role.roleId) || []
  )
  const [dropdownUsers, setDropdownUsers] = useState<RestSchemas['Messages.Projects.ProjectUser'][] | null>(null)

  const onOptionSelect = (_event: unknown, data: OptionOnSelectData) => {
    const user = (dropdownUsers ?? unassignedOrgUsers)?.find(
      (u) => `${u.givenName} ${u.familyName}` === data.optionValue
    )
    if (user) {
      setSelectedUserData(user)
    }
  }

  const handleSave = async () => {
    if (!selectedUserData) {
      toast.error('No user selected.')
      return
    }

    const mappedRoles: RestSchemas['Messages.Workspaces.MemberRole'][] = assignedRoleIds.map((roleId) => ({
      roleId,
      projectId,
      userId: selectedUserData.id,
    }))

    if (edit) {
      updateUser(
        { query: { projectId, userId: selectedUserData.id }, body: mappedRoles },
        {
          onSuccess: () => {
            toast.success('User updated successfully.')
            // Invalidate the query to refresh the users
            invalidateWorkspaceUsers()
            onSubmit?.()
          },
          onError: () => {
            toast.error('Failed to update user.')
          },
        }
      )
    } else {
      addUser(
        { body: mappedRoles },
        {
          onSuccess: () => {
            toast.success('User added successfully.')
            setDropdownUsers((prev) => prev?.filter((user) => user.id !== selectedUserData.id) ?? prev)
            // Invalidate the query to refresh the users
            invalidateWorkspaceUsers()
            onSubmit?.()
          },
          onError: () => {
            toast.error('Failed to add user.')
          },
        }
      )
    }
  }

  return (
    <div className="add-user-to-workspace-form">
      {!edit && (
        <div className="container">
          <label>Select User</label>
          <Dropdown placeholder="Select a user" onOptionSelect={onOptionSelect}>
            {unassignedOrgUsersPending ? (
              <Loading />
            ) : unassignedOrgUsersError ? (
              <ErrorComponent />
            ) : (
              (dropdownUsers ?? unassignedOrgUsers).map((user) => (
                <Option key={user.id} value={`${user.givenName} ${user.familyName}`}>
                  {`${user.givenName} ${user.familyName}`}
                </Option>
              ))
            )}
          </Dropdown>
        </div>
      )}
      {selectedUserData && (
        <>
          {appRolesPending && <Loading />}
          {appRolesError && <ErrorComponent />}
          {mappedAppRoles && (
            <>
              <div className="form-wrapper">
                <div className="form-row roles-assignment">
                  <RolesAssignmentComponent
                    title={edit ? 'Manage Roles' : 'Assign Roles'}
                    userRoles={mappedAppRoles}
                    onRolesChange={setAssignedRoleIds}
                    assignedRoleIds={assignedRoleIds}
                    disabledRoleIds={[]}
                  />
                </div>
              </div>
              <div className="action-buttons">
                <Button
                  disabled={addUserPending || updateUserPending}
                  text={edit ? 'Save' : 'Add User'}
                  onClick={handleSave}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
